/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2020] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Cookie from 'js-cookie';

class AltoConfig {
  constructor(configObj) {
    // Shared config values (external and internal)
    this.emsApplicationURL = configObj.emsApplicationURL;
    this.gatewayProtocol = configObj.gatewayProtocol;
    this.gatewayHost = configObj.gatewayHost;
    this.organizationName = configObj.organizationName;
    this.instanceIdentifier = configObj.instanceIdentifier;
    this.otdsUsernameCookieName = configObj.otdsUsernameCookieName;
    this.checkCookieName = configObj.checkCookieName;
    this.samlArtifactCookieName = configObj.samlArtifactCookieName;
    this.samlArtifactCookiePath = configObj.samlArtifactCookiePath;
    this.gatewayUrl = configObj.gatewayUrl;
    this.preLoginUrl = configObj.preLoginUrl;
    this.logMeOutUrl = configObj.logMeOutUrl;
    this.uploadGatewayUrl = configObj.uploadGatewayUrl;
    this.downloadGatewayUrl = configObj.downloadGatewayUrl;
    this.webContext = configObj.webContext;
    this.gatewayCookieDomain = configObj.gatewayCookieDomain;
    this.otdsProtocol = configObj.otdsProtocol;
    this.otdsHost = configObj.otdsHost;
    this.otdsRestUrl = configObj.otdsRestUrl;
    this.otdsTicketCookieName = configObj.otdsTicketCookieName;
    this.defaultNavigationTrigger = configObj.defaultNavigationTrigger;
    this.externalServiceAccountUserId = configObj.externalServiceAccountUserId;

    // Internal only configuration values
    this.iHubProtocol = configObj.iHubProtocol;
    this.iHubJSAPIHost = configObj.iHubJSAPIHost;
    this.otcsUrl = configObj.otcsUrl;

    // External only configuration values
    this.externalSessionCookieName = configObj.externalSessionCookieName;
    this.registerUrl = configObj.registerUrl;
    this.forgotUserNameUrl = configObj.forgotUserNameUrl;
    this.forgotPasswordUrl = configObj.forgotPasswordUrl;
    this.commandName = configObj.commandName;
    this.bannerText = configObj.bannerText;
  }

  /**
   * Gets the AppWorks web service URL for this environment.
   * @returns {string} the AppWorks web service URL
   * @example
   * Config.getWebServiceUrl()
   * -> http://appworkscloud05.c20g.com/home/ems
   */
  getWebServiceUrl() {
    return `${this.gatewayProtocol}${this.gatewayHost}/home/${this.organizationName}`;
  }

  /**
   * Gets the AppWorks organization DN for this environment.
   * @returns {string} the AppWorks organization DN
   * @example
   * Config.getOrganizationDN()
   * -> o=rfx,cn=cordys,cn=dev,o=lrxl2er3sbrerj32qpklrgqi5a.bx.internal.cloudapp.net
   */
  getOrganizationDN() {
    return `o=${this.organizationName},${this.instanceIdentifier}`;
  }

  /**
   * Gets the AppWorks organization name for this environment.
   * @returns {string} the AppWorks organization name
   * @example
   * Config.getOrganizationName()
   * -> rfx
   */
  getOrganizationName() {
    return this.organizationName;
  }

  /**
   * Gets the AppWorks check cookie name.
   * @returns {string} the AppWorks check cookie name
   * @example
   * Config.getCheckCookieName()
   * -> dev_ct
   */
  getCheckCookieName() {
    return this.checkCookieName;
  }

  /**
   * Gets the AppWorks SAML artifact cookie name.
   * @returns {string} the AppWorks SAML artifact cookie name
   * @example
   * Config.getSamlArtifactCookieName()
   * -> dev_SAMLart
   */
  getSamlArtifactCookieName() {
    return this.samlArtifactCookieName;
  }

  /**
   * Gets the AppWorks SAML artifact cookie path.
   * @returns {string} the AppWorks SAML artifact cookie path
   * @example
   * Config.getSamlArtifactCookieName()
   * -> /
   */
  getSamlArtifactCookiePath() {
    return this.samlArtifactCookiePath;
  }

  /**
   * Gets the AppWorks PreLoginInfo endpoint URL for this environment.
   * @returns {string} the AppWorks PreLoginInfo endpoint URL
   * @example
   * Config.getPreLoginUrl()
   * -> http://appworkscloud05.c20g.com/home/ems/com.eibus.sso.web.authentication.PreLoginInfo.wcp
   */
  getPreLoginUrl() {
    return `${this.getWebServiceUrl()}${this.preLoginUrl}`;
  }

  /**
   * Gets the AppWorks LogMeOut endpoint URL for this environment.
   * @returns {string} the AppWorks LogMeOut endpoint URL
   * @example
   * Config.getLogMeOutUrl()
   * -> http://appworkscloud05.c20g.com/home/ems/wcp/sso/com.eibus.sso.web.authentication.LogMeOut.wcp
   */
  getLogMeOutUrl() {
    return `${this.getWebServiceUrl()}${this.logMeOutUrl}`;
  }

  /**
   * Gets the AppWorks standard gateway URL for this environment.
   * @returns {string} the AppWorks standard gateway URL
   * @example
   * Config.getGatewayUrl()
   * -> http://appworkscloud05.c20g.com/home/ems/com.eibus.web.soap.Gateway.wcp
   */
  getGatewayUrl() {
    const base = `${this.getWebServiceUrl()}${this.gatewayUrl}`;
    const checkCookieName = this.getCheckCookieName();
    const checkCookieValue = Cookie.get(checkCookieName);
    const orgName = encodeURIComponent(this.getOrganizationDN());
    if (checkCookieValue) {
      return `${base}?${checkCookieName}=${checkCookieValue}&organization=${orgName}`;
    }
    return `${base}?organization=${orgName}`;
    // return base;
  }

  /**
   * Gets the AppWorks upload Gateway URL for this environment.
   * @returns {string} the AppWorks upload gateway URL
   * @example
   * Config.getUploadGatewayUrl()
   * -> http://appworkscloud05.c20g.com/home/ems/com.eibus.web.tools.upload.Upload.wcp
   */
  getUploadGatewayUrl() {
    const base = `${this.getWebServiceUrl()}${this.uploadGatewayUrl}`;
    const checkCookieName = this.getCheckCookieName();
    const checkCookieValue = Cookie.get(checkCookieName);
    if (checkCookieValue) {
      const orgName = encodeURIComponent(this.getOrganizationDN());
      return `${base}?${checkCookieName}=${checkCookieValue}&organization=${orgName}`;
    }
    return base;
  }

  /**
   * Gets the AppWorks Download Gateway URL for this environment.
   * @returns {string} the AppWorks download gateway URL
   * @example
   * Config.getDownloadGatewayUrl()
   * -> http://appworkscloud05.c20g.com/home/ems/com.eibus.web.tools.upload.Upload.wcp
   */
  getDownloadGatewayUrl() {
    const base = `${this.getWebServiceUrl()}${this.downloadGatewayUrl}`;
    const checkCookieName = this.getCheckCookieName();
    const checkCookieValue = Cookie.get(checkCookieName);
    if (checkCookieValue) {
      const orgName = encodeURIComponent(this.getOrganizationDN());
      return `${base}?${checkCookieName}=${checkCookieValue}&organization=${orgName}`;
    }
    return base;
  }

  /**
   * Gets the landing page URL for this environment.
   * @returns {string} the landing page URL
   * @example
   * Config.getLandingUrl()
   * -> http://appworkscloud05.c20g.com/
ems*/
  getLandingUrl() {
    return `${this.gatewayProtocol}${this.gatewayHost}`;
  }

  /**
   * Gets the AppWorks Gateway cookie domain for this environment.
   * @returns {string} the AppWorks gateway cookie domain
   * @example
   * Config.getGatewayCookieDomain()
   * -> c20g.com
   */
  getGatewayCookieDomain() {
    return this.gatewayCookieDomain;
  }

  /**
   * Gets the OTDS login form URL for this environment.
   * @returns {string} the OTDS login form URL
   * @example
   * Config.getOTDSLoginForm()
   * -> http://otds.c20g.com:8080/otdsws/login
   */
  getOTDSLoginForm() {
    return `${this.otdsProtocol}${this.otdsHost}${this.otdsRestUrl}/login`;
  }

  /**
   * Gets the OTDS REST API URL for this environment.
   * @returns {string} the OTDS REST API URL
   * @example
   * Config.getOTDSRESTUrl()
   * -> http://otds.c20g.com:8080/otdsws/rest
   */
  getOTDSRESTUrl() {
    return `${this.otdsProtocol}${this.otdsHost}${this.otdsRestUrl}/rest`;
  }

  /**
   * Gets the OTDS REST API Auth endpoint URL for this environment.
   * @returns {string} the OTDS REST API Auth endpoint URL
   * @example
   * Config.getOTDSRESTAuthEndpoint()
   * -> http://otds.c20g.com:8080/otdsws/rest/authentication/credentials
   */
  getOTDSRESTAuthEndpoint() {
    return `${this.otdsProtocol}${this.otdsHost}${this.otdsRestUrl}/rest/authentication/credentials`;
  }

  /**
   * Gets the user's OTDS username cookie name for this environment.
   * @returns {string} the OTDS username cookie name
   * @example
   * Config.getOTDSUsernameCookieName()
   * -> otds_username
   */
  getOTDSUsernameCookieName() {
    return this.otdsUsernameCookieName;
  }

  /**
   * Gets the OTDS ticket cookie name for this environment.
   * @returns {string} the OTDS ticket cookie name
   * @example
   * Config.getOTDSTicketCookieName()
   * -> otds_ticket
   */
  getOTDSTicketCookieName() {
    return this.otdsTicketCookieName;
  }

  /**
   * Gets the base web context for this environment.
   * @returns {string} the base web context
   * @example
   * Config.getBaseWebContext()
   * -> /
   */
  getBaseWebContext() {
    return this.webContext;
  }

  /**
   * Gets the Alto default navigation event trigger for this environment.
   * @returns {string} the Alto default navigation event trigger
   * @example
   * Config.getDefaultNavigationTrigger()
   * -> show:home:dashboard
   */
  getDefaultNavigationTrigger() {
    return this.defaultNavigationTrigger;
  }

  /**
   * Gets the AppWorks external user login endpoint URL for this environment.
   * @returns {string} the AppWorks external user login endpoint URL
   * @example
   * Config.getExternalLogInUrl()
   * -> http://localhost:8081/home/ems?organization=ems&login=1
   */
  getExternalLogInUrl() {
    const encodedOrgDn = encodeURIComponent(this.getOrganizationDN());
    return `${this.getWebServiceUrl()}${this.gatewayUrl}?organization=${encodedOrgDn}&login=1`;
  }

  /**
   * Gets the AppWorks external user CAC login endpoint URL for this environment.
   *  Note: this url is only hit after being redirected from OTDS
   * @returns {string} the AppWorks external user login endpoint URL
   */
  getExternalOtdsLogInUrl(redirectURL) {
    const orgDN = encodeURIComponent(this.getOrganizationDN());
    const encRedirectURL = encodeURIComponent(redirectURL || this.emsApplicationURL);
    const cookieDomain = encodeURIComponent(this.gatewayCookieDomain);

    const valveInternalLoginUrl = `:${this.getWebServiceUrl()}${
      this.gatewayUrl
    }?organization=${orgDN}&loginexternal=1&domain=${cookieDomain}&redirectUrl=${encRedirectURL}`;
    const encodedUrl = encodeURIComponent(valveInternalLoginUrl);

    const otdsHost = ENVIRONMENT === 'local.ext' ? 'otds01.c20g.com' : this.otdsHost;
    return `${this.otdsProtocol}${otdsHost}${this.otdsRestUrl}/login?PostTicket=True&PostParams=True&RFA=${encodedUrl}`;
  }

  /**
   * Gets the AppWorks internal user CAC login endpoint URL for this environment.
   *  Note: this url is only hit after being redirected from OTDS
   * @returns {string} the OTDS login URL with the internal valve login endpoint as an RFA redirect parameter
   */
  getInternalCACLogInUrl(redirectURL) {
    const orgDN = encodeURIComponent(this.getOrganizationDN());
    const encRedirectURL = encodeURIComponent(redirectURL || this.emsApplicationURL);
    const cookieDomain = encodeURIComponent(this.gatewayCookieDomain);

    const valveInternalLoginUrl = `:${this.getWebServiceUrl()}${
      this.gatewayUrl
    }?organization=${orgDN}&intrequest=1&logincacinternal=1&domain=${cookieDomain}&redirectUrl=${encRedirectURL}`;
    const appBaseURL = encodeURIComponent(valveInternalLoginUrl);

    const otdsHost = ENVIRONMENT === 'local.int' ? 'otds01.c20g.com' : this.otdsHost;
    return `${this.otdsProtocol}${otdsHost}${this.otdsRestUrl}/login?PostTicket=True&PostParams=True&RFA=${appBaseURL}`;
  }

  /**
   * Gets the URL for registering with a CAC for this environment.
   * @returns {string} the OTDS login URL with the valve registration endpoint as an RFA redirect parameter
   */
  getCACRegistrationUrl() {
    const orgDN = encodeURIComponent(this.getOrganizationDN());
    const cacRegistrationRoute = `${encodeURIComponent(this.emsApplicationURL)}/#cac-register-page`;
    const cookieDomain = encodeURIComponent(this.gatewayCookieDomain);

    const valveLoginEndpoint = `:${this.getWebServiceUrl()}${
      this.gatewayUrl
    }?organization=${orgDN}&logincacexternal=1&domain=${cookieDomain}&redirectUrl=${cacRegistrationRoute}`;
    const encValveLoginEndpoint = encodeURIComponent(valveLoginEndpoint);

    const otdsHost = ENVIRONMENT === 'local.ext' ? 'otds01.c20g.com' : this.otdsHost;
    return `${this.otdsProtocol}${otdsHost}${this.otdsRestUrl}/login?PostTicket=True&PostParams=True&RFA=${encValveLoginEndpoint}`;
  }

  /**
   * Gets the AppWorks external user Registration endpoint URL for this environment.
   * @returns {string} the AppWorks external user Registration endpoint URL
   * @example
   * Config.getExternalRegistrationUrl()
   * -> http://localhost:8081/home/ems?organization=ems&register=1
   */
  getExternalRegistrationUrl() {
    const encodedOrgDn = encodeURIComponent(this.getOrganizationDN());
    return `${this.getWebServiceUrl()}${this.gatewayUrl}?organization=${encodedOrgDn}&register=1`;
  }

  /**
   * Gets the AppWorks external user Registration endpoint URL for this environment.
   * @returns {string} the AppWorks external user Registration endpoint URL
   * @example
   * Config.getExternalRegistrationUrl()
   * -> http://localhost:8081/home/ems?organization=ems&register=1
   */
  getExternalCACRegistrationUrl() {
    const encodedOrgDn = encodeURIComponent(this.getOrganizationDN());
    return `${this.getWebServiceUrl()}${this.gatewayUrl}?organization=${encodedOrgDn}&registercac=1`;
  }

  /**
   * Gets the AppWorks external user service account UserId.
   * @returns {string}
   * @example
   * Config.getExternalServiceAccountUserId()
   * -> emsextuserserviceaccount
   */
  getExternalServiceAccountUserId() {
    return config.externalServiceAccountUserId;
  }

  /**
   * Gets the external user session cookie name this environment.
   * @returns {string} the external session cookie name
   * @example
   * Config.getExternalSessionCookieName()
   * -> EMS_SESSION
   */
  getExternalSessionCookieName() {
    return this.externalSessionCookieName;
  }

  /**
   * Gets the new user registration URL for this environment.
   * @returns {string} the new user registration URL
   * @example
   * Config.getRegisterUrl()
   * -> https://
   */
  getRegisterUrl() {
    return this.registerUrl;
  }

  /**
   * Gets the forgot username URL for this environment.
   * @returns {string} the forgot username URL
   * @example
   * Config.getForgotUserNameUrl()
   * -> https://
   */
  getForgotUserNameUrl() {
    return this.forgotUserNameUrl;
  }

  /**
   * Gets the forgot password URL for this environment.
   * @returns {string} the forgot password URL
   * @example
   * Config.getForgotPasswordUrl()
   * -> https://
   */
  getForgotPasswordUrl() {
    return this.forgotPasswordUrl;
  }

  /**
   * Gets the Content Server SmartUI URL for this environment.
   * @param {string} nodeId the target Content Server Node ID
   * @returns {string} the Content Server SmartUI URL
   * @example
   * Config.getOTCSSmartUIUrl(1001)
   * -> http://cs.appworkscloud05.c20g.com/app/emses/1001
   */
  getOTCSSmartUIUrl(nodeId) {
    return `${this.otcsUrl}/app/nodes/${nodeId}`;
  }

  /**
   * Gets the Content Server document editor URL for this environment.
   * @param {string} nodeId the target Content Server Node ID for the document
   * @returns {string} the Content Server document editor URL
   * @example
   * Config.getCSFileEditorLink(1002)
   * -> http://cs.appworkscloud05.c20g.com?func=emst.Edit&nodeid=1001
   */
  getCSFileEditorLink(nodeId) {
    return `${this.otcsUrl}?func=Edit.Edit&nodeid=${nodeId}`;
  }

  /**
   * Gets the text to display, if any, on the UI for different environments
   * @returns {string} the banner text
   * @example
   * Config.getBannerText()
   * -> Development Environment
   */
  getBannerText() {
    return this.bannerText;
  }
}

const config = require(`./${ENVIRONMENT}.json`);

export default new AltoConfig(config);
